// ─── SYNTHÈSE du mois : le tableau Débits / Crédits / Total ───────
// Extraite de VueMois le 16/08 (Cédric) : la synthèse doit vivre AU-DESSUS
// de la ligne de recherche, dans l'en-tête FIXE du Suivi — même ordre de
// lecture que Budget et Comptes (carte, recherche, mois).
// Le périmètre des totaux suit le FILTRE d'enveloppes, jamais la recherche :
// un total qui change à chaque lettre tapée ne veut rien dire.
import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useUI, useT } from '@mop/core';
import { formatEuro } from './services/calculs';
import { occurrencesDuMois, imprevusDuMois, totauxMois } from './services/occurrences';
import { filtrerOccurrences, filtrerImprevus } from './services/filtres';

export default function SyntheseMois({
  mois, lignes = [], actuals = [], exceptions = [], rattachements = null, budgetIds = [],
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const totaux = useMemo(() => {
    const occ = occurrencesDuMois({ lignes, actuals, exceptions, rattachements, mk: mois });
    const imp = imprevusDuMois({ actuals, rattachements, mk: mois });
    return totauxMois(
      filtrerOccurrences(occ, { budgetIds }),
      filtrerImprevus(imp, { budgetIds }),
    );
  }, [lignes, actuals, exceptions, rattachements, mois, budgetIds]);

  const couleurEcart = totaux.ecart > 0.005 ? (UI.ok || '#2e7d32')
    : totaux.ecart < -0.005 ? (UI.danger || '#c0392b') : UI.muted;

  return (
    <View style={s.totaux}>
      <View style={s.ligneSynthese}>
        <Text style={[s.blocLbl, s.colGauche]} />
        <Text style={[s.blocLbl, s.colDroite]}>{t('finances.suiviPrevu')}</Text>
        <Text style={[s.blocLbl, s.colDroite]}>{t('finances.suiviReel')}</Text>
        <Text style={[s.blocLbl, s.colDroite]}>{t('finances.suiviEcart')}</Text>
      </View>
      {[
        { cle: 'debits', lbl: t('finances.graphDepenses'), signe: '− ',
          prevu: totaux.prevuDebit, reel: totaux.reelDebit, ecart: totaux.ecartDebit },
        { cle: 'credits', lbl: t('finances.graphRevenus'), signe: '+ ',
          prevu: totaux.prevuCredit, reel: totaux.reelCredit, ecart: totaux.ecartCredit },
      ].map(r => (
        <View key={r.cle} style={s.ligneSynthese}>
          <Text style={s.syntheseLbl}>{r.lbl}</Text>
          <Text style={s.syntheseVal}>{r.signe}{formatEuro(r.prevu)}</Text>
          <Text style={s.syntheseVal}>{r.signe}{formatEuro(r.reel)}</Text>
          <Text style={[s.syntheseVal, { color: r.ecart > 0.005 ? (UI.ok || '#2e7d32')
            : r.ecart < -0.005 ? (UI.danger || '#c0392b') : UI.muted }]}>
            {r.ecart > 0 ? '+' : ''}{formatEuro(r.ecart)}
          </Text>
        </View>
      ))}
      <View style={[s.ligneSynthese, s.ligneTotal]}>
        <Text style={[s.syntheseLbl, { fontWeight: '800' }]}>{t('finances.syntheseTotal')}</Text>
        <Text style={s.syntheseTot}>{formatEuro(totaux.prevu)}</Text>
        <Text style={s.syntheseTot}>{formatEuro(totaux.reel)}</Text>
        <Text style={[s.syntheseTot, { color: couleurEcart }]}>{formatEuro(totaux.ecart)}</Text>
      </View>
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  // COLONNE — jamais `row` : la leçon de la carte vide (0.6.55.1).
  totaux: { backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13, marginBottom: 10 },
  ligneSynthese: { flexDirection: 'row', alignItems: 'center', paddingVertical: 3 },
  ligneTotal: { borderTopWidth: 0.5, borderTopColor: UI.border, marginTop: 3, paddingTop: 6 },
  blocLbl: { fontSize: 11, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  colGauche: { flex: 0.7 },
  colDroite: { flex: 1.2, textAlign: 'right' },
  syntheseLbl: { flex: 0.7, fontSize: 12.5, color: UI.muted, fontWeight: '700' },
  syntheseVal: { flex: 1.2, fontSize: 12.5, color: UI.ink, fontWeight: '700', textAlign: 'right' },
  syntheseTot: { flex: 1.2, fontSize: 13, color: UI.ink, fontWeight: '800', textAlign: 'right' },
});
